import React from 'react';
import { ExtractedMorningstarData } from './analysis';

type EquityFlavorKey = keyof ExtractedMorningstarData['equityFlavor'];

interface QuantitativeAnalysisTableProps {
  data: ExtractedMorningstarData;
}

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  marginBottom: '1.5rem',
};

const headerCellStyle: React.CSSProperties = {
  border: '1px solid var(--ion-color-step-150, #d7d8da)',
  padding: '0.5rem',
  textAlign: 'left',
  fontWeight: 600,
  background: 'var(--ion-color-step-50, #f4f5f8)',
};

const cellStyle: React.CSSProperties = {
  border: '1px solid var(--ion-color-step-150, #d7d8da)',
  padding: '0.5rem',
  textAlign: 'left',
};

const sectionStyle: React.CSSProperties = {
  marginBottom: '2rem',
};

const headingStyle: React.CSSProperties = {
  marginBottom: '0.75rem',
  fontSize: '1.05rem',
  fontWeight: 600,
};

const formatPercent = (value?: number | null) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '—';
  }
  return `${value.toFixed(1)}%`;
};

const formatNumber = (value?: number | null, unit?: string) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '—';
  }
  const formatted = value % 1 === 0 ? value.toString() : value.toFixed(2);
  return unit ? `${formatted} ${unit}` : formatted;
};

export const QuantitativeAnalysisTable: React.FC<QuantitativeAnalysisTableProps> = ({ data }) => {
  const { assetAllocation, fixedIncomeSectors, creditQualityBreakdown, stockSectors, effectiveBondDuration, northAmericanEquityPercentage, equityFlavor } = data;

  const hasAssetAllocation =
    assetAllocation &&
    ['cash', 'usStocks', 'nonUSStocks', 'bonds', 'other'].some(
      (key) => typeof assetAllocation[key as keyof typeof assetAllocation] === 'number'
    );

  const equityRows: Array<{ label: string; keys: EquityFlavorKey[] }> = [
    { label: 'Large Cap', keys: ['largeValue', 'largeBlend', 'largeGrowth'] },
    { label: 'Mid Cap', keys: ['midValue', 'midBlend', 'midGrowth'] },
    { label: 'Small Cap', keys: ['smallValue', 'smallBlend', 'smallGrowth'] },
  ];

  const hasEquityFlavor =
    equityFlavor &&
    equityRows.some((row) => row.keys.some((key) => typeof equityFlavor[key] === 'number'));

  const internationalPercentage =
    typeof northAmericanEquityPercentage === 'number'
      ? Math.max(0, 100 - northAmericanEquityPercentage)
      : null;

  return (
    <div>
      {hasAssetAllocation && (
        <section style={sectionStyle}>
          <h3 style={headingStyle}>Asset Allocation</h3>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={headerCellStyle}>Category</th>
                <th style={headerCellStyle}>Portfolio (%)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={cellStyle}>Cash</td>
                <td style={cellStyle}>{formatPercent(assetAllocation.cash)}</td>
              </tr>
              <tr>
                <td style={cellStyle}>US Stocks</td>
                <td style={cellStyle}>{formatPercent(assetAllocation.usStocks)}</td>
              </tr>
              <tr>
                <td style={cellStyle}>Non-US Stocks</td>
                <td style={cellStyle}>{formatPercent(assetAllocation.nonUSStocks)}</td>
              </tr>
              <tr>
                <td style={cellStyle}>Bonds</td>
                <td style={cellStyle}>{formatPercent(assetAllocation.bonds)}</td>
              </tr>
              <tr>
                <td style={cellStyle}>Other</td>
                <td style={cellStyle}>{formatPercent(assetAllocation.other)}</td>
              </tr>
              <tr>
                <td style={cellStyle}>Total Equity</td>
                <td style={cellStyle}>
                  {formatPercent(
                    (assetAllocation.usStocks ?? 0) + (assetAllocation.nonUSStocks ?? 0)
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </section>
      )}

      {typeof northAmericanEquityPercentage === 'number' && (
        <section style={sectionStyle}>
          <h3 style={headingStyle}>Geographic Exposure</h3>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={headerCellStyle}>Region</th>
                <th style={headerCellStyle}>Portfolio (%)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={cellStyle}>North America</td>
                <td style={cellStyle}>{formatPercent(northAmericanEquityPercentage)}</td>
              </tr>
              <tr>
                <td style={cellStyle}>International</td>
                <td style={cellStyle}>{formatPercent(internationalPercentage)}</td>
              </tr>
            </tbody>
          </table>
        </section>
      )}

      {Array.isArray(fixedIncomeSectors) && fixedIncomeSectors.length > 0 && (
        <section style={sectionStyle}>
          <h3 style={headingStyle}>Fixed-Income Sectors</h3>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={headerCellStyle}>Sector</th>
                <th style={headerCellStyle}>Portfolio (%)</th>
              </tr>
            </thead>
            <tbody>
              {fixedIncomeSectors.map(({ sector, portfolioPercentage }, index) => (
                <tr key={`${sector}-${index}`}>
                  <td style={cellStyle}>{sector}</td>
                  <td style={cellStyle}>{formatPercent(portfolioPercentage)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {Array.isArray(creditQualityBreakdown) && creditQualityBreakdown.length > 0 && (
        <section style={sectionStyle}>
          <h3 style={headingStyle}>Credit Quality Breakdown</h3>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={headerCellStyle}>Credit Rating</th>
                <th style={headerCellStyle}>Portfolio (%)</th>
              </tr>
            </thead>
            <tbody>
              {creditQualityBreakdown.map(({ creditRating, portfolioPercentage }, index) => (
                <tr key={`${creditRating}-${index}`}>
                  <td style={cellStyle}>{creditRating}</td>
                  <td style={cellStyle}>{formatPercent(portfolioPercentage)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {Array.isArray(stockSectors) && stockSectors.length > 0 && (
        <section style={sectionStyle}>
          <h3 style={headingStyle}>Stock Sectors</h3>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={headerCellStyle}>Category</th>
                <th style={headerCellStyle}>Sector</th>
                <th style={headerCellStyle}>Portfolio (%)</th>
              </tr>
            </thead>
            <tbody>
              {stockSectors.map(({ category, sector, portfolioPercentage }, index) => (
                <tr key={`${category}-${sector}-${index}`}>
                  <td style={cellStyle}>{category}</td>
                  <td style={cellStyle}>{sector}</td>
                  <td style={cellStyle}>{formatPercent(portfolioPercentage)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {hasEquityFlavor && (
        <section style={sectionStyle}>
          <h3 style={headingStyle}>Equity Style (Morningstar 9-Box)</h3>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={headerCellStyle}>Market Cap</th>
                <th style={headerCellStyle}>Value</th>
                <th style={headerCellStyle}>Blend</th>
                <th style={headerCellStyle}>Growth</th>
              </tr>
            </thead>
            <tbody>
              {equityRows.map(({ label, keys }) => (
                <tr key={label}>
                  <td style={cellStyle}>{label}</td>
                  {keys.map((key) => (
                    <td style={cellStyle} key={key}>
                      {formatPercent(equityFlavor[key])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {typeof effectiveBondDuration === 'number' && (
        <section style={sectionStyle}>
          <h3 style={headingStyle}>Fixed-Income Duration</h3>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={headerCellStyle}>Metric</th>
                <th style={headerCellStyle}>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={cellStyle}>Effective Duration</td>
                <td style={cellStyle}>{formatNumber(effectiveBondDuration, 'years')}</td>
              </tr>
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
};

export default QuantitativeAnalysisTable;

